
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, ExternalLink, Loader2, AlertTriangle, Globe } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface InstallationTestProps {
  pixelId: string;
  onTestPixel?: () => Promise<void>;
}

export function InstallationTest({ pixelId, onTestPixel }: InstallationTestProps) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasRecentActivity, setHasRecentActivity] = useState<boolean | null>(null);
  const [websiteCheckResult, setWebsiteCheckResult] = useState<any>(null);

  const handleTestPixel = async () => {
    setTesting(true);
    setTestResult(null);
    setHasRecentActivity(null);

    try {
      console.log("Starting pixel test for ID:", pixelId);
      
      const { data: testData, error: testError } = await supabase.functions.invoke('test-visitor-email', {
        body: { pixelId }
      });
      
      if (testError) {
        console.error("Error calling test-visitor-email function:", testError);
        throw testError;
      }
      
      console.log("Test visitor email function response:", testData);
      
      if (onTestPixel) {
        await onTestPixel();
      }
      
      if (testData && testData.success === true) {
        console.log("Test successful! Email notification sent.");
        setTestResult('success');
        toast.success("Pixel is working! Test email notification sent.");
        
        if (testData && testData.details?.websiteCheckResult) {
          setWebsiteCheckResult(testData.details.websiteCheckResult);
        }
      } else {
        console.error("Test unsuccessful:", testData);
        setTestResult('error');
        
        if (testData && testData.error) {
          const errorMsg = testData.error.message || JSON.stringify(testData.error);
          toast.error(`Test failed: ${errorMsg}`);
        } else {
          toast.error("Test failed. Please check your installation.");
        }
      }
    } catch (err) {
      console.error('Test pixel error:', err);
      setTestResult('error');
      toast.error("Failed to test pixel. Please check your installation.");
    } finally {
      setTesting(false);
    }
  };

  const handleValidateInstallation = () => {
    if (!pixelId) {
      toast.error("Cannot validate: Pixel ID is missing");
      return;
    }
    
    setIsValidating(true);
    
    supabase.functions.invoke('test-visitor-email', {
      body: { 
        pixelId,
        validateOnly: true,
        checkWebsite: true
      }
    }).then(({ data }) => {
      if (data && data.success) {
        setHasRecentActivity(data.details?.hasRecentActivity || false);
        
        if (data.details?.hasRecentActivity) {
          toast.success(`Active pixel: ${data.details.recentVisitCount} real visits in last 24h`);
        } else {
          toast.warning("No real visits detected. Please check your installation.");
        }
        
        if (data.details?.websiteCheckResult) {
          setWebsiteCheckResult(data.details.websiteCheckResult);
        }
      }
      
      const validatorUrl = `/validator?pixel_id=${pixelId}`;
      
      console.log("Opening validator URL:", validatorUrl);
      
      window.open(validatorUrl, '_blank');
    })
    .catch(err => {
      console.error("Error validating pixel:", err);
      toast.error("Failed to check pixel activity");
    })
    .finally(() => {
      setIsValidating(false);
    });
  };

  return (
    <div className="space-y-4">
      <Separator />
      <h3 className="text-sm font-medium">3. Test your installation</h3>
      <p className="text-sm text-muted-foreground">
        After adding the code to your website and loading the page at least once, click the button below to verify it's working correctly.
      </p>
      
      {hasRecentActivity === false && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3">
          <div className="flex gap-2 items-start">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
            <p className="text-sm text-amber-700">
              No real visits detected in the last 24h. Please make sure your pixel is properly installed and your website has received traffic.
            </p>
          </div>
        </div>
      )}
      
      {websiteCheckResult && (
        <div className={`border rounded p-3 mb-3 ${websiteCheckResult.pixelFound ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex gap-2 items-start">
            {websiteCheckResult.pixelFound ? (
              <Check size={16} className="text-green-600 mt-0.5" />
            ) : (
              <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
            )}
            <div className="text-sm">
              {websiteCheckResult.pixelFound ? (
                <p className="text-green-700">
                  Pixel found on your website! The installation is correct.
                </p>
              ) : websiteCheckResult.anyPixelFound ? (
                <p className="text-amber-700">
                  A pixel was found on your website, but with a different ID than expected.
                </p>
              ) : websiteCheckResult.error ? (
                <p className="text-amber-700">
                  Could not check website: {websiteCheckResult.error}
                </p>
              ) : (
                <p className="text-amber-700">
                  Pixel not found on your website. Make sure to add the tracking code to the &lt;head&gt; section.
                </p>
              )}
              {websiteCheckResult.url && (
                <p className="text-xs mt-1 text-slate-500">
                  Checked URL: {websiteCheckResult.url}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="button"
          variant={testResult === 'success' ? 'secondary' : testResult === 'error' ? 'destructive' : 'default'}
          className={`flex-1 ${testResult === 'success' ? 'bg-green-500 hover:bg-green-600 text-white' : testResult === null ? 'bg-black hover:bg-black/90 text-white' : ''}`}
          onClick={handleTestPixel}
          disabled={testing}
        >
          {testing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : testResult === 'success' ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Test Passed
            </>
          ) : testResult === 'error' ? (
            'Test Failed'
          ) : (
            'Test Pixel'
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={handleValidateInstallation}
          disabled={isValidating}
        >
          {isValidating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Globe className="w-4 h-4 mr-2" />
          )}
          Check Website
        </Button>
      </div>
      
      {testResult === 'error' && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
          <h4 className="text-sm font-medium text-amber-800">Troubleshooting Tips</h4>
          <ul className="text-sm text-amber-700 mt-2 list-disc pl-5 space-y-1">
            <li>Make sure the pixel script is added to the <code>&lt;head&gt;</code> section of your website</li>
            <li>Verify your website has loaded at least once after adding the pixel</li>
            <li>Check that your browser isn't blocking the tracking script</li>
            <li>Clear your browser cache and try again</li>
          </ul>
        </div>
      )}
    </div>
  );
}
